import React, { Suspense, type ReactNode } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuth } from './context/AuthContext.tsx'
import Layout from './components/Layout.tsx'
import { NotificationCenter } from './components/NotificationCenter.tsx'

const LoginPage = React.lazy(() => import('./pages/LoginPage.tsx'))
const LicensePage = React.lazy(() => import('./pages/LicensePage.tsx'))
const DashboardPage = React.lazy(() => import('./pages/DashboardPage.tsx'))
const CarsPage = React.lazy(() => import('./pages/DriversPage.tsx'))
const DevicesPage = React.lazy(() => import('./pages/DevicesPage.tsx'))
const LogsPage = React.lazy(() => import('./pages/LogsPage.tsx'))
const ActivationsPage = React.lazy(() => import('./pages/ActivationsPage.tsx'))
const NewsPage = React.lazy(() => import('./pages/NewsPage.tsx'))
const UsersPage = React.lazy(() => import('./pages/UsersPage.tsx'))
const OverlaysPage = React.lazy(() => import('./pages/OverlaysPage.tsx'))
const CarAssetsPage = React.lazy(() => import('./pages/CarAssetsPage.tsx'))
const CS75AnalyticsPage = React.lazy(() => import('./pages/CS75AnalyticsPage.tsx'))
const BuildsPage = React.lazy(() => import('./pages/BuildsPage.tsx'))
const ADBPage = React.lazy(() => import('./pages/ADBPage.tsx'))
const Q07AdbPage = React.lazy(() => import('./pages/Q07AdbPage.tsx'))
const ClientHomePage = React.lazy(() => import('./pages/ClientHomePage.tsx'))
const ClientNewsPage = React.lazy(() => import('./pages/ClientNewsPage.tsx'))
const ProgressPage = React.lazy(() => import('./pages/ProgressPage.tsx'))

// Where an authenticated user lands by default — the client role has no
// business on /cars (activator's VIN registry, 403s for them server-side).
function defaultRouteForRole(role: string | undefined): string {
  return role === 'client' ? '/client' : '/cars'
}

function AdminOnlyRoute({ children }: { children: ReactNode }) {
  const { isAdmin, user } = useAuth()
  return isAdmin ? <>{children}</> : <Navigate to={defaultRouteForRole(user?.role)} replace />
}
// An unauthenticated visitor landing on any *protected* path (a stale
// bookmark to /client, /cars, the bare root, anything) bounces out to the
// real public entry point instead of surfacing the staff login form -
// admin.drivebox.pro isn't meant to be a public landing experience. /login
// itself stays reachable by typing it directly (it's routed separately as
// its own PublicRoute below, untouched by this) - that's the "explicit"
// staff path.
function AnonymousFallback() {
  window.location.replace('https://drivebox.pro/cabinet')
  return null
}
function ProtectedRoute({ children }: { children: ReactNode }) {
  const { isAuthenticated, loading } = useAuth()
  if (loading) {
    return <Fallback />
  }
  return isAuthenticated ? <>{children}</> : <AnonymousFallback />
}

function PublicRoute({ children }: { children: ReactNode }) {
  const { isAuthenticated, loading, user } = useAuth()
  if (loading) {
    return <Fallback />
  }
  return !isAuthenticated ? <>{children}</> : <Navigate to={defaultRouteForRole(user?.role)} replace />
}

function Fallback() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-sm text-slate-500">Загрузка...</div>
    </div>
  )
}

function AdminApplication() {
  const { user } = useAuth()
  const fallbackRoute = defaultRouteForRole(user?.role)

  return (
    <>
      <Layout>
        <Routes>
          <Route path="/" element={<Navigate to={fallbackRoute} replace />} />
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/cars" element={<CarsPage />} />
          <Route path="/devices" element={<DevicesPage />} />
          <Route path="/logs" element={<LogsPage />} />
          <Route path="/activations" element={<ActivationsPage />} />
          <Route path="/news" element={<NewsPage />} />
          <Route path="/users" element={<UsersPage />} />
          <Route path="/overlays" element={<OverlaysPage />} />
          <Route path="/car-assets" element={<CarAssetsPage />} />
          <Route path="/cluster-analytics" element={<CS75AnalyticsPage />} />
          <Route path="/builds" element={<BuildsPage />} />
          <Route path="/adb" element={<AdminOnlyRoute><ADBPage /></AdminOnlyRoute>} />
          <Route path="/q07-adb" element={<AdminOnlyRoute><Q07AdbPage /></AdminOnlyRoute>} />
          <Route path="/client" element={<ClientHomePage />} />
          <Route path="/client/news" element={<ClientNewsPage />} />
          <Route path="/client/download" element={<Navigate to="/client" replace />} />
          <Route path="/progress" element={<ProgressPage />} />
          <Route path="*" element={<Navigate to={fallbackRoute} replace />} />
        </Routes>
      </Layout>
      {user?.role !== 'client' && (
        <div className="fixed right-4 top-2.5 z-50 md:right-6">
          <NotificationCenter username={user?.username || 'admin'} />
        </div>
      )}
    </>
  )
}

export default function App() {
  return (
    <div className="page-fade-in" style={{ display: 'contents' }}>
      <Suspense fallback={<Fallback />}>
        <Routes>
          <Route
            path="/login"
            element={
              <PublicRoute>
                <LoginPage />
              </PublicRoute>
            }
          />
          {/* admin.drivebox.pro is strictly the staff surface - client/
              partner login+registration lives on drivebox.pro/cabinet, this
              is just here for anyone who still has the old URL bookmarked. */}
          <Route path="/cabinet" element={<Navigate to="/login" replace />} />
          <Route
            path="/license"
            element={<LicensePage />}
          />
          <Route
            path="/*"
            element={
              <ProtectedRoute>
                <AdminApplication />
              </ProtectedRoute>
            }
          />
        </Routes>
      </Suspense>
    </div>
  )
}
